/**
 * 「全渠道套图」的渠道语义(2026-07-29 T4)。
 *
 * ═══ 为什么按渠道而不是按比例 ═══════════════════════════════════════════════
 * 用户心智是「我要发抖音 / 小红书 / 朋友圈」,不是「我要 9:16 / 3:4 / 1:1」。
 * 卡面叫「全渠道套图」,进去却让人勾比例 —— 名字和界面说的不是一回事。
 * 比例在这里降为**实现细节**:选渠道 → 映射比例 → 去重 → 得到 ratios。
 *
 * ★两个渠道可以映射到同一个比例(朋友圈 / 淘宝主图 都是 1:1)。去重是必须的:
 *   同比例出两张一模一样的图,用户付两份钱拿到重复品。
 * ★老的比例多选没有删,仍在 ParamsPopover 里当「高级」入口 —— 存量口径不动。
 */
import type { Ratio } from "./useFreeCreateForm";

export interface ChannelPreset {
  key: string;
  label: string;
  ratio: Ratio;
  /** 卡面副标题:说清楚这个比例在那个渠道是什么位置 */
  hint: string;
  /** chip 合并时用的短名(同比例渠道会被拼成一个 chip,长 label 拼起来读不动) */
  short: string;
}

export const CHANNEL_PRESETS: ChannelPreset[] = [
  { key: "douyin", label: "抖音 / 视频号", ratio: "9:16", hint: "竖版全屏", short: "抖音/视频号" },
  { key: "xiaohongshu", label: "小红书", ratio: "3:4", hint: "笔记首图", short: "小红书" },
  // P2-2(0829 琳琳):朋友圈 1:1→9:16(竖版全屏),与抖音同组自动合并(同图两处能用);
  // 公众号拆出去归 16:9(头图横版)——顺带修掉它与 ai 侧 _PLATFORM_RATIO(公众号=16:9)的旧不一致。
  { key: "moments", label: "朋友圈", ratio: "9:16", hint: "竖版全屏", short: "朋友圈" },
  { key: "taobao", label: "淘宝 / 京东主图", ratio: "1:1", hint: "商品主图", short: "淘宝主图" },
  // gzh 放 web 前:16:9 首现位置不变 → CHANNEL_GROUPS 比例顺序(9:16,3:4,1:1,16:9)不动,出图顺序稳定。
  { key: "gzh", label: "公众号", ratio: "16:9", hint: "头图横版", short: "公众号" },
  { key: "web", label: "官网 / 横幅", ratio: "16:9", hint: "横版 banner", short: "官网横幅" },
];

/**
 * 界面上真正的可勾选单位 —— **按比例合并后的渠道组**(2026-07-30,7.29 反馈任务 5)。
 *
 * ═══ 为什么要合并 ═══════════════════════════════════════════════════════════
 * 用户报「朋友圈/公众号 和 淘宝/京东主图 都是 1:1,取消其中一个没反应」。
 * 上一轮(07-29 T4)是按 `CHANNEL_PRESETS` 逐条出 chip、选中态靠「这个比例在不在
 * ratioSet 里」反推 —— 于是**同比例的两个 chip 共享同一个状态位**,取消任一个都要去问
 * 「是不是还有别的渠道在用这个比例」,而那个判断在取消分支里恒为真(见下),什么都不会发生。
 *
 * 真因不是那行判断写错,是**渠道没有自己的选中状态**。而这个状态本来就不该存在:
 * 勾「朋友圈」和勾「淘宝主图」出的是**同一张 1:1 图**(channelsToRatios 会去重),
 * 分成两个 chip 等于在暗示会出两张。合并成一个 chip 之后,「组 ↔ 比例」是 1:1 的,
 * 选中态无歧义,取消就是取消,不需要任何「还有谁在用」的判断。
 *
 * 组内成员仍留在 CHANNEL_PRESETS 里:回显(ratiosToChannels)、摘要(channelSummary)、
 * 以及 chip 的 title 悬浮说明都要按渠道粒度讲话。
 */
export interface ChannelGroup {
  ratio: Ratio;
  /** 合并 chip 文案,如「朋友圈/公众号 · 淘宝主图」 */
  label: string;
  /** 悬浮全文:逐渠道的 label + hint */
  title: string;
  /** 组内渠道 key(顺序同 CHANNEL_PRESETS) */
  keys: string[];
}

export const CHANNEL_GROUPS: ChannelGroup[] = (() => {
  const byRatio = new Map<Ratio, ChannelPreset[]>();
  for (const c of CHANNEL_PRESETS) {
    const list = byRatio.get(c.ratio);
    if (list) list.push(c);
    else byRatio.set(c.ratio, [c]);
  }
  // 顺序跟 CHANNEL_PRESETS 首次出现的顺序(Map 保插入序)—— 出图顺序稳定的同一条理由。
  return [...byRatio.entries()].map(([ratio, members]) => ({
    ratio,
    label: members.map((m) => m.short).join(" · "),
    title: members.map((m) => `${m.label}(${m.hint})`).join(" / ") + ` — ${ratio}`,
    keys: members.map((m) => m.key),
  }));
})();

/** 默认勾选:覆盖竖 / 方 / 竖窄三种最常投放的形态。 */
export const DEFAULT_CHANNEL_KEYS = ["douyin", "xiaohongshu", "moments"];

/**
 * 渠道 keys → ratios(去重、保持渠道顺序)。
 *
 * 返回顺序 = CHANNEL_PRESETS 的顺序,不是用户点选顺序 —— 出图顺序稳定,
 * 用户重开一次拿到的排列一样(详情页成品条按 seq 排,顺序抖动会让人以为出错了)。
 */
export function channelsToRatios(keys: string[]): Ratio[] {
  const picked = new Set(keys);
  const out: Ratio[] = [];
  for (const c of CHANNEL_PRESETS) {
    if (picked.has(c.key) && !out.includes(c.ratio)) out.push(c.ratio);
  }
  return out;
}

/** ratios → 渠道 keys(回显用:草稿/预设回来的是比例,界面要把渠道勾上)。 */
export function ratiosToChannels(ratios: Ratio[] | null): string[] {
  if (!ratios?.length) return [];
  return CHANNEL_PRESETS.filter((c) => ratios.includes(c.ratio)).map((c) => c.key);
}

/** 某组比例覆盖了哪些渠道的可读描述(底栏 / 回显列用)。 */
export function channelSummary(ratios: Ratio[] | null): string {
  const keys = ratiosToChannels(ratios);
  if (!keys.length) return "";
  const labels = CHANNEL_PRESETS.filter((c) => keys.includes(c.key)).map((c) => c.label);
  return labels.join(" · ");
}
